import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Result, Space, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import PageTitle from 'components/page-title';
import TabbedPage from 'components/tabbed-page';
import {
  STUDIO_PAGES,
  getMonitorSummary,
  getRequestsSummary,
  listStudios,
  useStudioViewer,
  useStudioLoad,
} from './api';
import { describeStudioError } from './studio-error';
import { NO_STUDIO_TITLE, STUDIO_ERROR_TEXT } from './labels';
import CountPill from './components/count-pill';
import StudioListTab from './list/studio-list-tab';
import RequestsTab from './list/requests-tab';
import MonitorTab from './list/monitor-tab';
import StudioFormModal from './list/studio-form-modal';
import styles from './styles.module.scss';

const TITLE = 'Studio';
/** S2 nhận limit 1–50: bộ lọc Studio của hai tab Đề nghị / Giám sát lấy tối đa 50 Studio (xếp theo tên). */
const FILTER_STUDIOS_LIMIT = 50;

/**
 * `/studios` — trang Studio của Admin và SMOD (SPEC Studio mục 8.1, 8.7.1, 8.7.2, 8.7.10). Ba tab qua `?tab=`:
 * `list` (mặc định) danh sách Studio, `requests` đề nghị chờ duyệt, `monitor` giám sát cờ + nhật ký liên
 * Studio. Quyền hiện nút lấy từ GET /studios/mine (useStudioViewer, mục 10.4); mọi quyết định quyền do máy
 * chủ kiểm. Quản lý Studio được chuyển về `/studio` (Studio của mình); Quản lý chưa có Studio và người khác
 * thấy lời giải thích.
 */
const StudioListPage = () => {
  const viewer = useStudioViewer();
  const [params] = useSearchParams();
  const tab = params.get('tab');
  const ready = !!viewer.mine && viewer.canViewAll;

  const requestsSummary = useStudioLoad(getRequestsSummary, [], { enabled: ready });
  const monitorSummary = useStudioLoad(getMonitorSummary, [], { enabled: ready });
  // Danh sách Studio cho bộ lọc của hai tab Đề nghị / Giám sát: chỉ tải khi mở một trong hai tab.
  const [studiosKey, setStudiosKey] = useState(0);
  const filterStudios = useStudioLoad(
    () => listStudios({ limit: FILTER_STUDIOS_LIMIT, sort: 'title' }),
    [studiosKey],
    { enabled: ready && (tab === 'requests' || tab === 'monitor') }
  );
  const [listKey, setListKey] = useState(0);
  const [creating, setCreating] = useState(false);

  const refreshCounts = () => {
    requestsSummary.reload();
    monitorSummary.reload();
  };
  /** Sau khi tạo / sửa / đổi Quản lý / xoá Studio. */
  const afterStudioChange = () => {
    setStudiosKey((k) => k + 1);
    refreshCounts();
  };

  if (viewer.loading && !viewer.mine) {
    return (
      <div className={styles.page}>
        <PageTitle title={TITLE} />
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      </div>
    );
  }

  if (!viewer.mine) {
    const view = describeStudioError(viewer.error);
    if (view.kind === 'unavailable') return <Result status="warning" title={view.message} />;
    return (
      <div className={styles.page}>
        <PageTitle title={TITLE} />
        <Alert
          type="error"
          showIcon
          message={view.message}
          action={
            <Button size="small" onClick={viewer.reload}>
              Thử lại
            </Button>
          }
        />
      </div>
    );
  }

  // Quản lý Studio: trang của họ là /studio → /studio/<id> (mine.tsx).
  if (viewer.isManager) return <Navigate to={STUDIO_PAGES.mine} replace />;

  if (!viewer.canViewAll) {
    const noStudio = viewer.kind === 'OPS_NO_STUDIO';
    return (
      <Result
        status="403"
        title={noStudio ? NO_STUDIO_TITLE : STUDIO_ERROR_TEXT.STUDIO_ROLE_NOT_ALLOWED}
        subTitle={noStudio ? STUDIO_ERROR_TEXT.STUDIO_NOT_MANAGER : undefined}
        extra={
          <Link to="/">
            <Button>Về Tổng quan</Button>
          </Link>
        }
      />
    );
  }

  // Lỗi tải (503, 409, mất mạng) không nuốt: hai tab hiện câu lỗi + "Thử lại" cạnh bộ lọc (mục 8.7).
  const filterOptions = filterStudios.data?.data || [];

  return (
    <>
      <TabbedPage
        title={TITLE}
        extra={
          viewer.canAdmin ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
              Tạo Studio
            </Button>
          ) : undefined
        }
        tabs={[
          {
            key: 'list',
            label: 'Danh sách Studio',
            render: () => (
              <StudioListTab viewer={viewer} reloadKey={listKey} onChanged={afterStudioChange} />
            ),
          },
          {
            key: 'requests',
            label: (
              <Space size={6}>
                <span>Đề nghị chờ duyệt</span>
                <CountPill count={requestsSummary.data?.total} hideZero />
              </Space>
            ),
            render: () => (
              <RequestsTab
                viewer={viewer}
                studios={filterOptions}
                studiosError={filterStudios.error}
                onReloadStudios={filterStudios.reload}
                onChanged={refreshCounts}
              />
            ),
          },
          {
            key: 'monitor',
            label: (
              <Space size={6}>
                <span>Giám sát</span>
                <CountPill count={monitorSummary.data?.openFlags} hideZero />
              </Space>
            ),
            render: () => (
              <MonitorTab
                viewer={viewer}
                studios={filterOptions}
                studiosError={filterStudios.error}
                onReloadStudios={filterStudios.reload}
                onChanged={refreshCounts}
              />
            ),
          },
        ]}
      />
      <StudioFormModal
        open={creating}
        onClose={() => setCreating(false)}
        onDone={() => {
          setListKey((k) => k + 1);
          afterStudioChange();
        }}
      />
    </>
  );
};

export default StudioListPage;
